import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CoverageNetworkTypeEnum } from './enums/coverage-network-type.enum';
import { InsurancePlanLevelEnum } from './enums/insurance-plan-level.enum';
import { InsurancePlanTypeEnum } from './enums/insurance-plan-type.enum';
import { MemberInsuranceConsentEnum } from '@genesis-frontend/genesis-utilities';
import {
    InsurancePlanAccumulators, InsurancePlanAccumulatorsCoverage,
    InsurancePlanCoverage, InsurancePlanCoverageAmount
} from '@genesis-frontend/graphql';

@Component({
    selector: 'insurance-plan-coverage',
    templateUrl: './insurance-plan-coverage.component.html',
    styleUrls: ['./insurance-plan-coverage.component.scss']
})
export class InsurancePlanCoverageComponent implements OnChanges {

    @Input() insurancePlanCoverage: InsurancePlanCoverage | undefined = {} as InsurancePlanCoverage;
    @Input() insurancePlanAccumulators: InsurancePlanAccumulators | undefined = {} as InsurancePlanAccumulators;
    @Input() planType = '';
    @Input() memberInsuranceConsent: MemberInsuranceConsentEnum = MemberInsuranceConsentEnum.NoResponse;
    @Input() isLoading = false;

    @Output() openConsentModalChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    insurancePlanCoverages: InsurancePlanCoverageAmount[] = [];

    accumulatorData: InsurancePlanAccumulatorsCoverage[] = [];

    coverageNetworkOptions: any[] = [
        { name: this.translations.instant(CoverageNetworkTypeEnum.InNetwork) },
        { name: this.translations.instant(CoverageNetworkTypeEnum.OutOfNetwork) }
    ];
    medicalCoverages = {
        individualCoverage: {
            inNetwork: [] as InsurancePlanCoverageAmount[],
            outOfNetwork: [] as InsurancePlanCoverageAmount[]
        },
        familyCoverage: {
            inNetwork: [] as InsurancePlanCoverageAmount[],
            outOfNetwork: [] as InsurancePlanCoverageAmount[]
        }
    };
    rbpCoverages = {
        individualCoverage: [] as InsurancePlanCoverageAmount[],
        familyCoverage: [] as InsurancePlanCoverageAmount[]
    };
    dentalCoverages = {
        individualCoverage: [] as InsurancePlanCoverageAmount[],
        familyCoverage: [] as InsurancePlanCoverageAmount[],
        noPlanLevelCoverage: [] as InsurancePlanCoverageAmount[]
    };
    coverageLoaded = false;
    visionCoverages = [] as InsurancePlanCoverageAmount[];
    currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.InNetwork);
    networkFilter = CoverageNetworkTypeEnum.InNetwork;
    insurancePlanLevel = InsurancePlanLevelEnum;
    hasError = false;
    openModal = false;
    showLearnMore = false;
    isMedicalIndividualFamilyPlan = false;
    isMedicalRBPPlan = false;
    accumulatorsState = false;
    hasCoverageData = {
        medical: {
            inNetwork: {
                individual: false,
                family: false
            },
            outOfNetwork: {
                individual: false,
                family: false
            }
        },
        rbp: {
            individual: false,
            family: false
        },
        dental: {
            individual: false,
            family: false,
            noPlanLevel: false
        },
        vision: {
            noPlanLevel: false
        }
    };
    showAccumulator = {
        individual: false,
        family: false
    };
    dentalAccumulators = false;

    isTierredPlan = false;

    readonly constants = {
        NO_NETWORK: 'None',
        RBP: 'RBP',
        NO_PLAN_LEVEL: 'None',
        INDIVIDUAL_DEDUCTIBLE_DENTAL: 'IndividualDeductibleOnly',
        FAMILY_DEDUCTIBLE_DENTAL: 'FamilyDeductibleOnly',
        CONTACT_OR_FRAMES_LENSES: 'ContactOrFramesLenses',
        CONTACT_FRAMES_LENSES: 'ContactFramesLenses',
        ACCUMULATORS: 'Accumulators',
        DEDUCTIBLE: 'Deductible',
        OUT_OF_POCKET_MAX: 'OutOfPocketMax',
        DATE_FORMAT: 'MMMM dd, yyyy',
        TIER: 'Tier'
    };

    constructor(
        private translations: TranslateService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['memberInsuranceConsent']) {
            this.checkMemberInsuranceConsent();
        }
        this.accumulatorData = this.mapAccumulators();
        this.getData(this.planType);
    }

    getData(planType: string) {
        switch (planType) {
        case InsurancePlanTypeEnum.Medical:
            this.checkMedical();
            break;
        case InsurancePlanTypeEnum.Dental:
            this.checkDental();
            break;
        case InsurancePlanTypeEnum.Vision:
            this.checkVision();
            break;
        }

        this.checkHasError();
    }

    checkMedical() {
        if (this.insurancePlanCoverage?.type !== this.constants.RBP) {
            this.setMedicalValues();
        } else {
            this.setupRBPCoverage();
            this.networkFilter = CoverageNetworkTypeEnum.OutOfNetwork;
        }

        if (this.planType === InsurancePlanTypeEnum.Medical) {
            this.setAccumulators();
        }

        this.checkMedicalPlanCoverage();
        this.checkDropdownOptions();
    }

    checkMedicalPlanCoverage() {
        this.isMedicalIndividualFamilyPlan =
            this.planType === InsurancePlanTypeEnum.Medical &&
            this.insurancePlanCoverage?.type !== this.constants.RBP;
        this.isMedicalRBPPlan = this.planType === InsurancePlanTypeEnum.Medical &&
            this.insurancePlanCoverage?.type === this.constants.RBP;

        this.insurancePlanCoverages = this.mapCoverageAmounts();
        this.isTierredPlan = this.insurancePlanCoverages.filter((coverage) => coverage.network.includes(this.constants.TIER)).length > 0;
    }

    setMedicalValues() {
        this.setupMedicalIndividualFamilyValues(CoverageNetworkTypeEnum.OutOfNetwork);
        this.setupMedicalIndividualFamilyValues(CoverageNetworkTypeEnum.InNetwork);
    }

    setupMedicalIndividualFamilyValues(network: string) {
        this.getMedicalCoverages(InsurancePlanLevelEnum.Individual, network);
        this.getMedicalCoverages(InsurancePlanLevelEnum.Family, network);
    }

    checkHasMedical(network: string) {
        if (network === CoverageNetworkTypeEnum.InNetwork) {
            this.hasCoverageData.medical.inNetwork.individual = this.medicalCoverages.individualCoverage.inNetwork.length > 0;
            this.hasCoverageData.medical.inNetwork.family = this.medicalCoverages.familyCoverage.inNetwork.length > 0;
        } else if (network === CoverageNetworkTypeEnum.OutOfNetwork) {
            this.hasCoverageData.medical.outOfNetwork.individual = this.medicalCoverages.individualCoverage.outOfNetwork.length > 0;
            this.hasCoverageData.medical.outOfNetwork.family = this.medicalCoverages.familyCoverage.outOfNetwork.length > 0;
        }
    }

    checkMedicalAccumulator() {
        this.accumulatorData = this.mapAccumulators();
        this.accumulatorData.forEach((accumulator) => {
            if (accumulator.network === this.networkFilter && accumulator.level === InsurancePlanLevelEnum.Individual) {
                this.showAccumulator.individual = true;
            } else if (accumulator.network === this.networkFilter && accumulator.level === InsurancePlanLevelEnum.Family) {
                this.showAccumulator.family = true;
            }
        });
    }

    setAccumulators() {
        this.checkMedicalAccumulator();
        this.checkAccumulatorsState();
    }

    checkAccumulatorsState() {
        this.accumulatorsState = this.insurancePlanCoverage?.showAccumulators === this.constants.ACCUMULATORS && this.accumulatorData.length > 0;
    }

    setupRBPCoverage() {
        this.getRbpCoverage(InsurancePlanLevelEnum.Individual);
        this.getRbpCoverage(InsurancePlanLevelEnum.Family);
    }

    checkDental() {
        const planLevels = [
            InsurancePlanLevelEnum.Individual,
            InsurancePlanLevelEnum.Family,
            InsurancePlanLevelEnum.NoPlanLevel
        ];

        planLevels.forEach((planLevel) => {
            this.getDentalCoverages(planLevel);
        });

        this.checkAccumulatorsState();
        this.dentalAccumulators = this.insurancePlanCoverage?.showAccumulators === this.constants.ACCUMULATORS && this.accumulatorData.length > 0;
    }

    checkVision() {
        this.getVisionCoverages();
    }

    mapCoverageAmounts(): InsurancePlanCoverageAmount[] {
        return (this.insurancePlanCoverage?.amounts || []).filter((amount): amount is InsurancePlanCoverageAmount => amount != null);
    }

    mapAccumulators(): InsurancePlanAccumulatorsCoverage[] {
        return (this.insurancePlanAccumulators?.accumulatorData || []).filter((data): data is InsurancePlanAccumulatorsCoverage => data != null);
    }

    getCoverageDataByPlanLevelAndNetwork(planLevel: string, network: string): InsurancePlanCoverageAmount[] {
        return this.mapCoverageAmounts().filter(
            (amount) =>
                amount.planLevel === planLevel &&
                amount.network === network
        ).sort((a, b) => a.coverage.length - b.coverage.length);
    }

    getMedicalCoverages(planLevel: string, network: string) {
        this.getCoverageDataByPlanLevelAndNetwork(planLevel, network);
        if (planLevel === InsurancePlanLevelEnum.Individual) {
            this.setMedicalIndividualCoverage(network);
        } else if (planLevel === InsurancePlanLevelEnum.Family) {
            this.setMedicalFamilyCoverage(network);
        }
        this.checkHasMedical(network);
    }

    checkDropdownOptions() {
        if (this.checkIfAccumulatorsAreEnabled()) {
            this.setupAccumulatorOffDropdown();
        } else if (this.insurancePlanCoverage?.showAccumulators === this.constants.ACCUMULATORS && this.accumulatorData.length > 0) {
            this.setupAccumulatorsDropdown();
        }
    }

    checkIfAccumulatorsAreEnabled() {
        return (this.insurancePlanCoverage?.showAccumulators !== this.constants.ACCUMULATORS) ||
            (this.insurancePlanCoverage?.showAccumulators === this.constants.ACCUMULATORS && this.accumulatorData.length === 0);
    }

    setupAccumulatorOffDropdown() {
        if (this.medicalCoverages.individualCoverage.inNetwork.length === 0 && this.medicalCoverages.familyCoverage.inNetwork.length === 0 && !this.isTierredPlan) {
            this.coverageNetworkOptions.shift();
            this.currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.OutOfNetwork);
            this.networkFilter = CoverageNetworkTypeEnum.OutOfNetwork;
        } else if (this.medicalCoverages.individualCoverage.outOfNetwork.length === 0 && this.medicalCoverages.familyCoverage.outOfNetwork.length === 0) {
            this.coverageNetworkOptions.pop();
            this.currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.InNetwork);
            this.networkFilter = CoverageNetworkTypeEnum.InNetwork;
        }
    }

    setupAccumulatorsDropdown() {
        if (this.filterAccumulatorsByNetwork(CoverageNetworkTypeEnum.InNetwork).length === 0) {
            this.coverageNetworkOptions.shift();
            this.currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.OutOfNetwork);
            this.networkFilter = CoverageNetworkTypeEnum.OutOfNetwork;
        } else if (this.filterAccumulatorsByNetwork(CoverageNetworkTypeEnum.OutOfNetwork).length === 0) {
            this.coverageNetworkOptions.pop();
            this.currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.InNetwork);
            this.networkFilter = CoverageNetworkTypeEnum.InNetwork;
        }
    }

    filterAccumulatorsByNetwork(network: string) {
        return this.mapAccumulators().filter(
            (accumulator) =>
                accumulator.network === network
        );
    }

    setMedicalIndividualCoverage(network: string) {
        if (network === CoverageNetworkTypeEnum.InNetwork) {
            this.medicalCoverages.individualCoverage.inNetwork = this.setMedicalCoverage(InsurancePlanLevelEnum.Individual, network);
        } else if (network === CoverageNetworkTypeEnum.OutOfNetwork) {
            this.medicalCoverages.individualCoverage.outOfNetwork = this.setMedicalCoverage(InsurancePlanLevelEnum.Individual, network);
        }
    }

    setMedicalFamilyCoverage(network: string) {
        if (network === CoverageNetworkTypeEnum.InNetwork) {
            this.medicalCoverages.familyCoverage.inNetwork = this.setMedicalCoverage(InsurancePlanLevelEnum.Family, network);
        } else if (network === CoverageNetworkTypeEnum.OutOfNetwork) {
            this.medicalCoverages.familyCoverage.outOfNetwork = this.setMedicalCoverage(InsurancePlanLevelEnum.Family, network);
        }
    }

    setMedicalCoverage(planLevel: string, network: string) {
        if (network === CoverageNetworkTypeEnum.InNetwork) {
            return this.getCoverageDataByPlanLevelAndNetwork(planLevel, CoverageNetworkTypeEnum.InNetwork);
        } else if (network === CoverageNetworkTypeEnum.OutOfNetwork) {
            return this.getCoverageDataByPlanLevelAndNetwork(planLevel, CoverageNetworkTypeEnum.OutOfNetwork);
        }
        return [];
    }

    getRbpCoverage(planLevel: string) {
        if (planLevel === InsurancePlanLevelEnum.Individual) {
            this.rbpCoverages.individualCoverage = this.getCoverageDataByPlanLevelAndNetwork(planLevel, CoverageNetworkTypeEnum.OutOfNetwork);
            this.hasCoverageData.rbp.individual = this.rbpCoverages.individualCoverage.length > 0;
        } else if (planLevel === InsurancePlanLevelEnum.Family) {
            this.rbpCoverages.familyCoverage = this.getCoverageDataByPlanLevelAndNetwork(planLevel, CoverageNetworkTypeEnum.OutOfNetwork);
            this.hasCoverageData.rbp.family = this.rbpCoverages.familyCoverage.length > 0;
        }
    }

    getDentalCoverages(planLevel: InsurancePlanLevelEnum) {
        const coverageData = this.getCoverageDataByPlanLevelAndNetwork(planLevel, this.constants.NO_NETWORK);
        const hasData = coverageData.length > 0;
        switch (planLevel) {
        case InsurancePlanLevelEnum.Individual:
            this.dentalCoverages.individualCoverage = coverageData;
            this.hasCoverageData.dental.individual = hasData;
            break;
        case InsurancePlanLevelEnum.Family:
            this.dentalCoverages.familyCoverage = coverageData;
            this.hasCoverageData.dental.family = hasData;
            break;
        case InsurancePlanLevelEnum.NoPlanLevel:
            this.dentalCoverages.noPlanLevelCoverage = coverageData;
            this.hasCoverageData.dental.noPlanLevel = hasData;
            break;
        }
    }

    getVisionCoverages() {
        this.visionCoverages = this.getCoverageDataByPlanLevelAndNetwork(InsurancePlanLevelEnum.NoPlanLevel, this.constants.NO_NETWORK);
        this.hasCoverageData.vision.noPlanLevel = this.visionCoverages.length > 0;
    }

    checkHasError() {
        this.hasError =
            (!this.insurancePlanCoverage ||
            !this.insurancePlanCoverage.amounts ||
            this.insurancePlanCoverage.amounts.length === 0) && !this.accumulatorsState;
    }

    dropdownChangeListener() {
        this.setAccumulators();
        if (this.coverageLoaded) {
            if (this.currentNetwork === this.translations.instant(CoverageNetworkTypeEnum.OutOfNetwork)) {
                this.currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.InNetwork);
                this.networkFilter = CoverageNetworkTypeEnum.InNetwork;
            } else if (this.currentNetwork === this.translations.instant(CoverageNetworkTypeEnum.InNetwork)) {
                this.currentNetwork = this.translations.instant(CoverageNetworkTypeEnum.OutOfNetwork);
                this.networkFilter = CoverageNetworkTypeEnum.OutOfNetwork;
                this.checkMedical();
            }
        }
        this.coverageLoaded = true;
    }

    checkMemberInsuranceConsent() {
        this.showLearnMore = this.memberInsuranceConsent !== MemberInsuranceConsentEnum.Accepted &&
            this.memberInsuranceConsent !== MemberInsuranceConsentEnum.OptedOut;
    }

    protected readonly InsurancePlanTypeEnum = InsurancePlanTypeEnum;
    protected readonly CoverageNetworkTypeEnum = CoverageNetworkTypeEnum;
}
